/**
 * Module dependencies.
 */

import { Button, Loading, Subtitle, Type } from 'src/components/core';
import { DownloadItem } from 'src/types/downloads';
import { GetStaticProps, NextPage } from 'next/types';
import { color, media, units } from '@untile/react-components';
import { fetchDownloads } from 'src/api/downloads/fetch-downloads';
import { ifProp, theme } from 'styled-tools';
import { useLocalizedRoute } from 'src/core/utils/routes';
import { useMemo } from 'react';
import Container from 'src/components/core/layout/container';
import Item from 'src/components/downloads/download-item';
import Metatags from 'src/components/core/metatags';
import groupBy from 'lodash/groupBy';
import map from 'lodash/map';
import sortBy from 'lodash/sortBy';
import styled, { css } from 'styled-components';
import useDownloads from 'src/api/downloads/use-downloads';
import useTranslate from 'src/hooks/use-translate';

/**
 * `Props` type.
 */

type Props = {
  downloads: DownloadItem[];
};

/**
 * `Wrapper` styled component.
 */

const Wrapper = styled.div<{ isLoading: boolean }>`
  background-color: ${color('backgrounds.taupe01')};
  padding: ${units(14)} 0 ${units(8)};
  position: relative;

  ${media.min('md')`
    padding: ${units(20)} 0 ${units(18)};
  `}

  ${ifProp(
    'isLoading',
    css`
      min-height: 100vh;
    `
  )}
`;

/**
 * `StyledContainer` styled component.
 */

const StyledContainer = styled(Container)`
  padding: 0;
`;

/**
 * `Grid` styled component.
 */

const Grid = styled.div`
  display: grid;
  grid-template-areas:
    '. title    .'
    '. subtitle .'
    '. button   .'
    '. files   .';
  grid-template-columns: ${theme('grid.gutterMobile')}px 1fr ${theme(
      'grid.gutterMobile'
    )}px;

  ${media.min('md')`
    grid-template-areas:
      '. title    title    title  .'
      '. subtitle subtitle button .'
      '. .        files    .      .';
    grid-template-columns: 1fr 2fr 6fr 2fr 1fr;
    grid-template-rows: repeat(3, max-content);
  `}
`;

/**
 * `Title` styled component.
 */

const Title = styled(Type.H1)`
  grid-area: title;

  ${media.min('md')`
    margin-bottom: ${units(1)};
  `}
`;

/**
 * `Description` styled component.
 */

const Description = styled(Subtitle)`
  grid-area: subtitle;

  ${media.max('md')`
    margin-bottom: ${units(3)};
  `}
`;

/**
 * `ButtonWrapper` styled component.
 */

const ButtonWrapper = styled.div`
  grid-area: button;

  ${media.min('md')`
    align-self: flex-end;
    display: flex;
    justify-self: flex-end;
  `}
`;

/**
 * `FilesWrapper` styled component.
 */

const FilesWrapper = styled.div`
  grid-area: files;
`;

/**
 * `FilesCategoryWrapper` styled component.
 */

const FilesCategoryWrapper = styled.div`
  display: flex;
  flex-direction: column;
  padding-top: 50px;

  ${media.min('md')`
    padding-top: ${units(9)};
  `}
`;

/**
 * `FilesCategory` styled component.
 */

const FilesCategory = styled(Subtitle).attrs({ size: 'large' })`
  margin-bottom: ${units(3)};

  ${media.min('md')`
    text-align: center;
  `}
`;

/**
 * `Downloads` page.
 */

const Downloads: NextPage<Props> = props => {
  const { translate } = useTranslate();
  const routeResolver = useLocalizedRoute();
  const buttonUrl = routeResolver('contactUs');
  const description = translate('downloads:description');
  const title = translate('downloads:title');
  const animProps = {
    'data-scroll': 'fade-in-up',
    'data-scroll-offset': '10%'
  };

  const {
    data: downloads,
    isLoading,
    isSuccess
  } = useDownloads({ initialData: props.downloads });

  const downloadsList = useMemo(() => {
    const groupCategories = groupBy(downloads, 'category.id');
    const downloadsList = map(groupCategories, downloads => {
      return {
        downloads,
        name: downloads[0].category.name
      };
    });

    return sortBy(downloadsList, 'name');
  }, [downloads]);

  return (
    <>
      <Metatags
        description={translate('downloads:description')}
        pageTitle={translate('downloads:title')}
        title={translate('downloads:title')}
      />

      <Wrapper isLoading={isLoading}>
        <Loading active={isLoading} />

        {isSuccess && (
          <StyledContainer>
            <Grid>
              <Title {...animProps}>{title}</Title>

              <Description {...animProps}>{description}</Description>

              <ButtonWrapper {...animProps}>
                <Button
                  colorTheme={'secondary'}
                  href={buttonUrl}
                  size={'large'}
                >
                  {translate('common:actions.reachOutToUs')}
                </Button>
              </ButtonWrapper>

              <FilesWrapper>
                {map(downloadsList, ({ downloads, name }, index) => (
                  <FilesCategoryWrapper key={index}>
                    <FilesCategory
                      {...animProps}
                      data-scroll-animation-delay={'150'}
                    >
                      {name}
                    </FilesCategory>

                    {map(downloads, ({ file, id, name }) => (
                      <div
                        {...animProps}
                        data-scroll-animation-delay={'150'}
                        key={id}
                      >
                        <Item
                          size={file.size}
                          title={name}
                          url={file.url}
                        />
                      </div>
                    ))}
                  </FilesCategoryWrapper>
                ))}
              </FilesWrapper>
            </Grid>
          </StyledContainer>
        )}
      </Wrapper>
    </>
  );
};

/**
 * Export `getStaticProps`.
 */

export const getStaticProps: GetStaticProps<Props> = async ({ locale }) => {
  const downloads = await fetchDownloads({ locale });

  if (!downloads) {
    return {
      notFound: true
    };
  }

  return {
    props: {
      downloads
    },
    revalidate: 60 * 60 * 24
  };
};

/**
 * Export `Downloads` page.
 */

export default Downloads;
